import { throwIfAborted } from '../abort.js';
import { bankFundingContract, isBankFundingMethod, providerFundingMethod, } from '../bank-transfers/types.js';
import { isDepositOperation, } from '../client.js';
import { FundingConnectionError } from '../customer/connection-error.js';
import { directPurchaseCredentials } from '../direct-purchase/credentials.js';
import { fundingOperationForCheckout } from '../operations/checkout-operation.js';
import { FundingProviderError } from '../provider-error.js';
import { resolveDeviceMethods } from './device-methods.js';
import { fundingMethodPolicy } from './manifest-policy.js';
import { FUNDING_METHODS, isFundingMethod, isHostedFundingMethod, isMethodEligible, } from './policy.js';
import { previouslyUsedCards } from './saved-cards.js';
// Provider identity scopes this memory-only record to one authenticated SDK client.
const authenticatedCustomers = new WeakMap();
export function rememberFundingCustomer(provider, customerId) {
    authenticatedCustomers.set(provider, customerId);
}
export function fundingCustomerId(provider) {
    return authenticatedCustomers.get(provider);
}
export function forgetFundingCustomer(provider) {
    authenticatedCustomers.delete(provider);
}
const unsupportedMethods = new WeakMap();
export function excludeFundingMethod(provider, method) {
    const methods = unsupportedMethods.get(provider) ?? new Set();
    methods.add(method);
    unsupportedMethods.set(provider, methods);
}
export function fundingMethodSupported(provider, method) {
    return !unsupportedMethods.get(provider)?.has(method);
}
export async function discoverFundingCheckout(options, signal, preparedContext) {
    const { client, provider, surface, deviceMethods, manifest } = options;
    const context = preparedContext ?? (await client.getContext(signal));
    throwIfAborted(signal);
    const pendingId = options.resumeOperationId ?? context.pendingId;
    const historical = pendingId ? (await client.getOperation(pendingId, signal)).data : null;
    const pending = historical &&
        (options.resumeOperationId || (isFundingMethod(historical.method) && !historical.withdrawal))
        ? fundingOperationForCheckout(historical)
        : null;
    if (pending && !isDepositOperation(pending))
        throw new Error('onramp_direction_mismatch');
    const currency = pending?.fiatCurrency ?? options.currency;
    if (pending && !['created', 'action_required'].includes(pending.status))
        return {
            context,
            pending,
            guest: false,
            status: 'available',
            methods: [],
            savedCards: [],
            needsContactVerification: false,
        };
    if ((options.directPurchase || pending?.purchase) && options.directPurchaseEnabled !== true)
        return {
            context,
            pending,
            guest: false,
            status: 'available',
            methods: [],
            savedCards: [],
            needsContactVerification: false,
            authenticationAvailable: false,
        };
    if (context.asset !== manifest.settings.settlement.provider_asset_code &&
        !(context.environment === 'sandbox' && context.asset === 'SOL'))
        throw new Error('onramp_settlement_policy_mismatch');
    // A remembered ID only selects the authenticated Customer API request. It is not
    // evidence of authentication: every discovery validates the live credentials.
    let customerId = authenticatedCustomers.get(provider);
    let customer = customerId ? await provider.getCustomer(customerId) : null;
    throwIfAborted(signal);
    if (customer && !customer.ok) {
        const failure = new FundingProviderError(customer.error);
        if (failure.failure.category !== 'connection_required')
            throw failure;
        forgetFundingCustomer(provider);
        customerId = undefined;
        customer = null;
    }
    const connection = customerId ? null : await provider.getConnection({ skipKyc: true });
    throwIfAborted(signal);
    if (connection && !connection.ok)
        throw new FundingConnectionError(connection.error);
    const value = connection?.value;
    if (value) {
        options.onConnectionCheck?.(value.status);
        client.observeLifecycle?.({ event: 'connection_checked', reason: value.status });
    }
    if (value?.status === 'active') {
        customerId = value.customer.id;
        customer = await provider.getCustomer(customerId);
        throwIfAborted(signal);
    }
    if (customerId) {
        if (!customer)
            throw new Error('onramp_customer_missing');
        if (!customer.ok) {
            const failure = new FundingProviderError(customer.error);
            if (failure.failure.category === 'connection_required')
                forgetFundingCustomer(provider);
            throw failure;
        }
        if (customer.value.data.id !== customerId) {
            forgetFundingCustomer(provider);
            throw new Error('onramp_customer_mismatch');
        }
        if (!connection)
            client.observeLifecycle?.({ event: 'provider_retained', reason: 'customer_refresh' });
        rememberFundingCustomer(provider, customerId);
    }
    const guestCandidate = !options.directPurchase &&
        !pending?.purchase &&
        !options.authenticationRequested &&
        value?.status === 'connectionRequired' &&
        value.mismatch !== true &&
        manifest.settings.guest_checkout.enabled &&
        context.guestEligible;
    // Missing verified contacts prevent MoonPay from offering guest capability.
    // Collect them first, then the host must create a newly attested session.
    if (value && value.status !== 'active' && value.status !== 'connectionRequired')
        return {
            context,
            pending,
            guest: false,
            status: value.status,
            methods: [],
            savedCards: [],
            needsContactVerification: false,
            authenticationAvailable: false,
        };
    const payer = guestCandidate ? await client.getPayerStatus(signal) : null;
    throwIfAborted(signal);
    const needsContactVerification = Boolean(payer &&
        (!payer.emailVerified ||
            !payer.phoneVerified ||
            (payer.consentRequired !== false && !payer.consentAccepted)));
    const guest = !options.directPurchase &&
        !pending?.purchase &&
        !options.authenticationRequested &&
        value?.status === 'connectionRequired' &&
        value.mismatch !== true &&
        manifest.settings.guest_checkout.enabled &&
        context.guestEligible &&
        Boolean(value.capabilities?.guestCheckout);
    const purchase = pending?.purchase ?? options.directPurchase?.binding;
    const purchaseContext = purchase
        ? await client.getDirectPurchaseContext(purchase, surface, signal, await directPurchaseCredentials(provider, signal))
        : null;
    throwIfAborted(signal);
    const policy = manifest.onramp.providers.moonpay;
    const directPolicy = manifest.direct_token_purchase.providers.moonpay;
    const directAllowed = (method) => !purchase ||
        ((method === 'apple_pay' || method === 'google_pay' || method === 'card') &&
            directPolicy.enabled &&
            directPolicy.methods[method].enabled &&
            purchaseContext?.destination === context.destination &&
            purchaseContext.purchase?.caip19 === purchase.caip19 &&
            purchaseContext.methods.includes(method));
    if (value?.status === 'connectionRequired' && !purchase && !guest && !needsContactVerification)
        return {
            context,
            pending,
            guest: false,
            status: value.status,
            // Listing a supported method does not authorize a disconnected payment.
            methods: FUNDING_METHODS.filter((method) => {
                const entry = fundingMethodPolicy(manifest, method);
                return (policy.enabled &&
                    entry.enabled &&
                    entry.surfaces.includes(surface) &&
                    fundingMethodSupported(provider, method));
            }),
            savedCards: [],
            needsContactVerification: false,
            authenticationAvailable: (purchase ? directPolicy.enabled : policy.enabled) &&
                (purchase
                    ? Object.values(directPolicy.methods)
                    : [
                        ...FUNDING_METHODS.map((method) => fundingMethodPolicy(manifest, method)),
                        policy.methods.cex_connect,
                        policy.methods.crypto_deposit,
                    ]).some((entry) => entry.enabled && entry.surfaces.includes(surface)),
            authenticationReason: value.mismatch === true
                ? 'account_mismatch'
                : !purchase && !context.guestEligible
                    ? 'region'
                    : 'provider',
        };
    // An unfiltered response includes fixed-currency rails even when the user's
    // display currency is USD. The quote still validates the exact destination.
    const result = await provider.getPaymentMethods();
    throwIfAborted(signal);
    if (!result.ok) {
        const failure = new FundingProviderError(result.error);
        if (failure.failure.category === 'connection_required')
            forgetFundingCustomer(provider);
        throw failure;
    }
    let customerVerification;
    if (customerId && customer?.ok) {
        const kyc = customer.value.data.kyc;
        customerVerification = {
            customerId,
            status: kyc.status,
            requirements: kyc.requirements,
            challenge: kyc.challenge ?? null,
        };
        if (kyc.status !== 'active' || kyc.challenge)
            return {
                context,
                pending,
                guest: false,
                status: 'available',
                methods: [],
                savedCards: [],
                needsContactVerification: false,
                customerVerification,
            };
    }
    const configs = result.value.data.paymentMethodConfigs ?? [];
    const methods = FUNDING_METHODS.filter((method) => {
        if (!directAllowed(method))
            return false;
        if (isBankFundingMethod(method) && context.asset !== 'USDC_SOL')
            return false;
        const hosted = isHostedFundingMethod(method);
        if ((hosted || isBankFundingMethod(method)) && !customerId)
            return false;
        const methodConfig = configs.find((config) => config.type === providerFundingMethod(method));
        const methodCurrency = bankFundingContract(method)?.currency ?? currency;
        const methodPolicy = purchase && (method === 'apple_pay' || method === 'google_pay' || method === 'card')
            ? directPolicy.methods[method]
            : fundingMethodPolicy(manifest, method);
        return isMethodEligible(methodPolicy, {
            platformEnabled: true,
            connected: !purchase && !isBankFundingMethod(method) && Boolean(customerId && customer?.ok),
            providerEnabled: purchase ? directPolicy.enabled : policy.enabled,
            surface,
            country: context.country,
            subdivision: context.subdivision,
            deviceSupported: (hosted ||
                isBankFundingMethod(method) ||
                method === 'card' ||
                resolveDeviceMethods(deviceMethods, context.environment).includes(method)) &&
                fundingMethodSupported(provider, method) &&
                (method === 'card' ||
                    !(guest || guestCandidate) ||
                    manifest.settings.guest_checkout.methods.includes(method)),
            providerEligible: Boolean(methodConfig?.availability.active &&
                methodConfig.capabilities.requiresWidget === hosted &&
                methodConfig.capabilities.supportedCurrencies.includes(methodCurrency) &&
                methodConfig.capabilities.supportedTransactionTypes.includes('buy')),
        });
    });
    return {
        ...(customerVerification ? { customerVerification } : {}),
        needsContactVerification,
        context,
        pending,
        guest,
        status: purchase && value?.status === 'connectionRequired' ? value.status : 'available',
        ...(purchase && value?.status === 'connectionRequired'
            ? {
                authenticationAvailable: true,
                authenticationReason: value.mismatch === true ? 'account_mismatch' : 'provider',
            }
            : {}),
        methods,
        savedCards: customerId && customer?.ok && methods.includes('card')
            ? previouslyUsedCards(result.value.data.paymentMethods ?? [], context.previouslyUsedPaymentMethodIds, configs.find((config) => config.type === 'card')?.capabilities.allowsDeletion === true)
            : [],
    };
}
